import React, { Fragment, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { createBusinessLendingRequest, getEligibilityStatus } from './service';
import Checkbox from '../checkbox/Checkbox';
import Button from '../button/Button';
import Error from '../error/Error';
import * as classes from './SmeLendingForm.module.scss';

function Tick() {
  return (
    <svg className={classes.statusIcon} width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
      <g fill="none" fillRule="evenodd">
        <rect fill="#A7F1E0" width="32" height="32" rx="16" />
        <path
          className={classes.tick}
          d="M9.986 17.287l3.224 3.268a1.65 1.65 0 002.497-.171l6.279-8.416h0"
          stroke="#192851"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

function Cross() {
  return (
    <svg className={classes.statusIcon} width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
      <g fill="none" fillRule="evenodd">
        <rect fill="#EA5C5C" width="32" height="32" rx="16" />
        <path
          className={classes.cross1}
          d="M10.972 10.936l10 11"
          stroke="#FFF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          className={classes.cross2}
          d="M21.117 10.936l-10 11"
          stroke="#FFF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

function Review({ config, data }) {
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [businessLendingRequestUid, setBusinessLendingRequestUid] = useState(null);
  const [eligible, setEligible] = useState(null);
  const [error, setError] = useState(null);
  const [ticks, setTicks] = useState(0);
  const ticksRef = useRef(ticks);

  useEffect(() => {
    ticksRef.current = ticks;
  }, [ticks]);

  useEffect(() => {
    if (businessLendingRequestUid) {
      let id = setInterval(() => {
        getEligibilityStatus(businessLendingRequestUid)
          .then((res) => res.json())
          .then((res) => {
            const eligibilityStatus = res.eligibilityStatus;

            if (ticksRef.current === 30 || eligibilityStatus !== 'PENDING_CHECKS') {
              const isEligible = eligibilityStatus !== 'NOT_ELIGIBLE';
              setEligible(isEligible);
              setBusinessLendingRequestUid(null);
              setLoading(false);
            } else {
              setTicks(ticksRef.current + 1);
            }
          })
          .catch(() => {
            setEligible(true);
            setBusinessLendingRequestUid(null);
            setLoading(false);
          });
      }, 1000);

      return () => clearInterval(id);
    }
  }, [businessLendingRequestUid]);

  if (loading) {
    return (
      <div className={classes.status}>
        <span className={classes.spinner} role="status" aria-hidden="true" />
        <h2>Processing your application...</h2>
      </div>
    );
  }

  if (typeof eligible === 'boolean') {
    return (
      <div className={classes.status}>
        {eligible ? (
          <Fragment>
            <Tick />
            <h2>We've received your application</h2>
          </Fragment>
        ) : (
          <Fragment>
            <Cross />
            <h2>Unfortunately you are not eligible for the Starling {config.productName}</h2>
          </Fragment>
        )}
      </div>
    );
  }

  return (
    <Fragment>
      <h2>Apply for a Starling business {config.productName}</h2>

      <small>
        By submitting your application, you’re confirming to us that you are a director or partner authorised to act on
        behalf of your business and consenting to us undertaking a new credit check on your profile, which includes a
        check on your personal credit file and one for your business entity. Where there are fewer than 4 directors in
        the business, you confirm that all Persons of Significant Control consent to us undertaking a credit check on
        both their business and personal credit files.
      </small>

      <small>
        All personal information that you give us will be stored and processed in accordance with our{' '}
        <a href="/legal/privacy-notice/" target="_blank" className={classes.link}>
          Privacy Notice
        </a>
        . We’ll be in touch soon about your application and might require more evidence of your business financials,
        including bank statements, your most recent year-end accounts and invoices.
      </small>

      <Checkbox
        id="confirmation"
        className={classes.confirmation}
        onChange={(event) => setConfirmation(event.target.checked)}
        checked={confirmation}
        label="I accept"
      />

      <Button
        disabled={!confirmation}
        onClick={() => {
          setLoading(true);
          createBusinessLendingRequest(data)
            .then((res) => {
              const uid = res.headers.get('location').match(/.*\/([^/]+)$/)[1];
              setBusinessLendingRequestUid(uid);
              setError(null);
            })
            .catch(() => {
              setError("We're sorry, something went wrong whilst submitting your application. Please try again");
              setLoading(false);
            });
        }}
      >
        Submit application
      </Button>

      {error && <Error>{error}</Error>}
    </Fragment>
  );
}
Review.propTypes = {
  config: PropTypes.object,
  data: PropTypes.object
};

export default Review;
